import { format } from "date-fns";
import { parseDateLocal } from "@/lib/utils";

// Suggested dates for the enrollment window a broker opens straight after an
// accepted quote creates the client's plans.
//
// The two dates mean different things and are easy to mix up:
//   startDate — when employees can begin choosing
//   endDate   — the enrollment DEADLINE, not when coverage starts
//
// The server (PUT /api/clients/:clientId/open-enrollment) rejects any selected
// plan whose coverage begins before that deadline, since employees would end up
// with no valid start date. Suggesting the day before coverage begins therefore
// validates on the first save instead of bouncing the broker off a 400.
//
// Dates are plain yyyy-mm-dd strings all the way through: that is what
// <Input type="date"> reads and writes, what the endpoint expects, and what
// storage hands back. Never build them with toISOString(), which shifts a day
// for anyone west of UTC.

/** How long employees get to choose, by default. */
export const DEFAULT_ENROLLMENT_WINDOW_DAYS = 14;

export type SuggestedEnrollmentWindow = {
  startDate: string;
  endDate: string;
};

const shiftDays = (date: Date, days: number): Date => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

/**
 * Suggest a window from the date coverage begins — the quote's effective date,
 * which is what the plans were created with.
 *
 * Returns null when there is no usable coverage start, so the caller can show
 * empty inputs rather than a made-up window.
 */
export function suggestEnrollmentWindow(
  coverageStart: string | Date | null | undefined,
  windowDays: number = DEFAULT_ENROLLMENT_WINDOW_DAYS,
): SuggestedEnrollmentWindow | null {
  const start = parseDateLocal(coverageStart as any);
  if (!start) return null;

  const deadline = shiftDays(start, -1);
  const opens = shiftDays(deadline, -Math.abs(windowDays));

  return {
    startDate: format(opens, "yyyy-MM-dd"),
    endDate: format(deadline, "yyyy-MM-dd"),
  };
}

/**
 * Adding plans to a window that already exists: keep every plan already in it
 * and add the new ones, rather than replacing the selection. Sending only the
 * new ids would drop whatever the client was already enrolling in.
 */
export function mergeSelectedPlanIds(
  existing: number[] | null | undefined,
  added: number[],
): number[] {
  return Array.from(new Set([...(existing ?? []), ...added]));
}
